import React, { FC, useContext, useMemo } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ConfirmModal, FormGenerator, ModalWrapper } from '@microservices/wiskey-react-components'
import { FormInput } from '@microservices/wiskey-react-components'
import { Add } from '@mui/icons-material'
import { Box, Button, FormHelperText, Grid, Typography } from '@mui/material'

import { BindTypeInputs } from '@components/BindTypeInputs'
import { ModalFormContainer } from '@components/ModalFormContainer'
import { TextSettings } from '@components/TextSettings'

import { GENERATOR_INPUT_TYPE } from '@constants'

import {
  PageContext,
  RESOURCE_COLUMN_FIELD_BINDING_TYPE,
} from '@gantt/components/GanttCreateOrEdit'
import { GANTT_RESOURCE_COLUMN_MAX_FIELDS_COUNT } from '@gantt/constants'
import { getBindTypeOptionsByEnum } from '@gantt/helpers'

import { useAddGanttFieldDialog } from './hooks/useAddGanttFieldDialog'

export const AddGanttFieldDialog: FC = () => {
  const {
    showDialog,
    handleCloseModal,
    modalType,
    existingPaths,
    selectedPaths,
    watchedObjectResource,
  } = useContext(PageContext)

  const {
    isDirty,
    isShowResetConfirmModal,
    columnInputs,
    columnHeaderSettingsRef,
    columnTextSettingsRef,
    initialColumnHeaderStyles,
    initialColumnTextStyles,
    watchUseParameters,
    otherColumnInputs,
    methods,
    handleHeaderExternalDirty,
    handleTextExternalDirty,
    handleClickReset,
    handleSave,
    handleResetParams,
    handleSetShowResetConfirmModal,
    handleAddField,
    handleDeleteField,
    fields,
    errors,
  } = useAddGanttFieldDialog()

  const { t } = useTranslation()

  const fieldPicker = useMemo(() => {
    return {
      selectedPaths,
      existingPaths,
    }
  }, [existingPaths, selectedPaths])

  const fieldsErrorSX = errors.fields?.message
    ? {
        border: '1px solid',
        borderColor: 'error.main',
      }
    : {}

  return (
    <ModalWrapper
      btnText={t('ganttCreate.common.apply')}
      disabledSave={!isDirty}
      isShow={showDialog}
      title={`${t(`addDialog.${modalType}`)} Column`}
      onClose={() => handleCloseModal(isDirty)}
      onSave={handleSave}
    >
      <ModalFormContainer maxHeight={500} minWidth={900}>
        <ConfirmModal
          actionBtnText={t('parameters.resetModal.actionBtnText')}
          isShow={isShowResetConfirmModal}
          text={t('parameters.resetModal.text')}
          title={t('parameters.resetModal.title')}
          containerStyle={{
            px: 0.5,
            py: 0.5,
          }}
          onClose={() => handleSetShowResetConfirmModal(false)}
          onConfirm={handleResetParams}
        />
        <FormProvider {...methods}>
          <form>
            <FormGenerator inputs={columnInputs} />
            <FormInput
              inputType={GENERATOR_INPUT_TYPE.INPUT}
              label={t('ganttCreate.columnForm.title')}
              name={'title'}
              placeholder={t('ganttCreate.columnForm.columnInputs.field.placeholder')}
            />

            <Box component='fieldset' mb={1} sx={fieldsErrorSX}>
              <legend>
                {errors.fields?.message ? (
                  <FormHelperText error sx={{ padding: 0, margin: 0, fontSize: 14 }}>
                    {`${t('ganttCreate.resourceForm.fields')}: ${errors.fields.message}`}
                  </FormHelperText>
                ) : (
                  t('ganttCreate.resourceForm.fields')
                )}
              </legend>

              {fields?.map((field, index) => {
                return (
                  <BindTypeInputs
                    key={`${index}_${field.id}`}
                    isFieldsetMarkup
                    isShowFormatDateField
                    bindTypeOptions={getBindTypeOptionsByEnum(RESOURCE_COLUMN_FIELD_BINDING_TYPE)}
                    containerName={`fields.${index}`} // поле Value c кнопкой Pick
                    fieldPicker={fieldPicker}
                    fieldsetTitle={`${t('ganttCreate.resourceForm.field')} ${index + 1}`}
                    isDisabled={!watchedObjectResource}
                    object={watchedObjectResource}
                    onDelete={() => handleDeleteField(index)}
                  />
                )
              })}
              {fields?.length < GANTT_RESOURCE_COLUMN_MAX_FIELDS_COUNT && (
                <Box display='flex' justifyContent='end' pb={1}>
                  <Button onClick={handleAddField}>
                    <Add fontSize='small' />
                    {t('ganttCreate.resourceForm.addField')}
                  </Button>
                </Box>
              )}
            </Box>

            <FormGenerator inputs={otherColumnInputs} />
            <Typography mb={2} variant='h6'>
              {t('addDialog.parameters')}
            </Typography>
            <TextSettings
              ref={columnHeaderSettingsRef}
              backgroundSettings={false}
              borderSettings={false}
              initialTextState={initialColumnHeaderStyles ? initialColumnHeaderStyles : undefined}
              isEdit={watchUseParameters}
              labelPlacement='top'
              labelText={t('parameters.visual.columnHeader')}
              onSetExternalDirty={handleHeaderExternalDirty}
            />
            <TextSettings
              ref={columnTextSettingsRef}
              borderSettings={false}
              initialTextState={initialColumnTextStyles ? initialColumnTextStyles : undefined}
              isEdit={watchUseParameters}
              labelPlacement='top'
              labelText={t('parameters.visual.columnText')}
              onSetExternalDirty={handleTextExternalDirty}
            />
            <Grid container item justifyContent='flex-end' my={2}>
              <Button variant='outlined' onClick={handleClickReset}>
                {t('parameters.resetButton')}
              </Button>
            </Grid>
          </form>
        </FormProvider>
      </ModalFormContainer>
    </ModalWrapper>
  )
}
